'use client'

// QueryProvider for TanStack Query - optimized for Connectors performance
// This replaces useEffect patterns with cached, optimized queries

import React from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

// Create optimized QueryClient instance
const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Global defaults for all queries
        staleTime: 1000 * 60 * 5, // 5 minutes default
        gcTime: 1000 * 60 * 10, // 10 minutes garbage collection
        retry: (failureCount, error) => {
          // Don't retry for authentication errors
          if (error instanceof Error && error.message.includes('auth')) {
            return false
          }
          // Retry up to 3 times for other errors
          return failureCount < 3
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        refetchOnWindowFocus: false, // Don't refetch on window focus for better performance
        refetchOnMount: true, // Refetch when component mounts
        refetchOnReconnect: true, // Refetch when network reconnects
      },
      mutations: {
        // Global defaults for all mutations
        retry: 1, // Retry mutations once on failure
        retryDelay: 1000, // 1 second delay before retry
      },
    },
  })
}

// Client-side only query client (avoid SSR hydration issues)
let clientQueryClient: QueryClient | undefined = undefined

const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server-side: always create a new query client
    return createQueryClient()
  } else {
    // Client-side: create query client if it doesn't exist
    if (!clientQueryClient) {
      clientQueryClient = createQueryClient()
    }
    return clientQueryClient
  }
}

interface QueryProviderProps {
  children: React.ReactNode
}

export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* Show dev tools only in development */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}

export default QueryProvider 